import { useEffect, useState } from "react";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { useAuth } from "stores/auth/auth-context";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";
import { IUseEligibleSetNickName } from "./use-eligible-set-nickname.types";

const NICKNAME_POPUP_SKIPPED_KEY = "nickNamePopupSkipped";

const isSkipCooldownActive = (): boolean => {
  try {
    const skipDataStr = localStorage.getItem(NICKNAME_POPUP_SKIPPED_KEY);
    if (!skipDataStr) return false;

    const skipData = JSON.parse(skipDataStr);
    const now = Date.now();

    // Check if cooldown period has expired
    if (skipData.expiresAt && now < skipData.expiresAt) {
      return true; // Still in cooldown
    }

    // Cooldown expired, remove the item
    localStorage.removeItem(NICKNAME_POPUP_SKIPPED_KEY);
    return false;
  } catch (err) {
    // If there's any error parsing, assume no cooldown
    return false;
  }
};

const useEligibleSetNickName = (): IUseEligibleSetNickName => {
  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    user: { isUserLoggedIn }
  } = useAuth();

  const { userSubscription, user } = useKelasLiveContext();
  const primaryUser = user?.userData?.primary_user || null;
  const { subscriptionStatus, isFetching } = userSubscription || {};
  const subscriptionType = subscriptionStatus?.subscriptionType;

  useEffect(() => {
    // Not eligible if not logged in or subscription is still fetching
    if (!isUserLoggedIn || isFetching) {
      return;
    }

    // Not eligible if not paid or has pending slot selection
    if (
      subscriptionType !== SubscriptionType.PAID ||
      subscriptionStatus?.pendingSlotSelection
    ) {
      setIsEligible(false);
      setIsLoading(false);
      return;
    }

    // Check nickName from primaryUser (from user-profiles API)
    const hasNickName = !!primaryUser?.nick_name;

    // Check if user has skipped recently
    const hasSkippedRecently = isSkipCooldownActive();

    // User is eligible only if they don't have a nickname AND haven't skipped recently
    setIsEligible(!hasNickName && !hasSkippedRecently);
    setIsLoading(false);
  }, [
    isUserLoggedIn,
    subscriptionType,
    subscriptionStatus?.pendingSlotSelection,
    primaryUser?.nick_name,
    isFetching
  ]);

  return {
    isEligibleSetNickName: isEligible,
    isLoading
  };
};

export default useEligibleSetNickName;
